/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { round } from 'lodash';
import { Fund } from '../../../components/Exchange/types';
import {
  AGREEMENT_TYPES,
  collegeMaxPlanLimits,
  EXCHANGE,
  UNIT,
  NEW_ICLASS_FUND_MIN_AMOUNT,
} from '../../../settings/settings';
import {
  isNewOrReopen,
  isProductType529,
  isRetirementFund,
} from '../../funds/funds-utility';
import { isAPP } from '../../advice/advice-utility';

/**
 * Returns dollar value of the exchange 'To' fund
 * @param {number} fromAmount
 * @param {ExchangeFund} fund
 * @param {number} sumOfSelected
 * @returns {number}
 */
export function getExchangeToDollarValue(
  fromAmount: number,
  fund: Fund,
  sumOfSelected: number
): number {
  const { grossAmount, grossUnit } = fund;
  switch (grossUnit) {
    case UNIT.DOLLARS:
      return grossAmount;
    case UNIT.PERCENT:
      return round(fromAmount * grossAmount) / 100;
    case UNIT.ALL_REMAINING:
      return fromAmount - sumOfSelected;
    default:
      return grossAmount;
  }
}

interface TransactionLimit {
  agreementTypeCategory: string;
  fundType: string;
  openingType: string;
  minimum: number;
}
/**
 * Gets the exchange minimum for funds being exchange to
 * @param {*} transactionLimits
 * @param {ExchangeFund} fund
 * @returns {number}
 */
export function getExchangeMinimum(
  transactionLimits: TransactionLimit[],
  fromFund: Fund,
  fund: Fund,
  newFundLimits: { minimum: number }
): number {
  const {
    agreementType,
    currentYearLimit,
    iClass,
    minimumInvestmentAmount,
    productType,
    summit,
    ticker,
  } = fund;
  const { sweep } = fromFund;
  const isRetirement: boolean =
    isRetirementFund(fund) || isRetirementFund(fromFund);
  const isNew: boolean = isNewOrReopen(fund);
  const isCollegeFund: boolean = isProductType529(productType);

  if (sweep) {
    return 0;
  }
  if (isCollegeFund) {
    const { minimum } = currentYearLimit.find(
      (l: { type: string }) => l.type === EXCHANGE
    ) || {
      minimum: 25,
    };
    return minimum;
  }

  if (isNew && newFundLimits && !summit && !iClass && ticker !== 'TRLAX') {
    return newFundLimits.minimum || 0;
  }

  const fundType = summit ? 'Summit' : iClass ? 'IClass' : 'Regular';
  const openingType = isNew ? 'NewAccount' : 'ExistingAccount';
  const agreementTypeCategory =
    agreementType === AGREEMENT_TYPES.UGMA_UTMA
      ? 'UGMA_AND_UTMA'
      : isRetirement
      ? 'IRA'
      : 'Regular';

  if (isAPP(fromFund.productType) && openingType === 'NewAccount') {
    return isRetirement ? 1000 : 2500;
  }

  const transactionLimitForFund = transactionLimits.filter(
    (limit) =>
      limit.agreementTypeCategory === agreementTypeCategory &&
      limit.fundType === fundType &&
      limit.openingType === openingType
  );

  // if adding a new iclass fund, will need to hard code minimum investment for now
  const specifiedMinimumInvestment = iClass
    ? NEW_ICLASS_FUND_MIN_AMOUNT
    : minimumInvestmentAmount;

  return isNew
    ? specifiedMinimumInvestment
    : transactionLimitForFund.length
    ? transactionLimitForFund[0].minimum
    : 0;
}

export const getCollegePlanLimit = (productType: string | number) => {
  return collegeMaxPlanLimits[productType];
};
